import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { 
  LayoutDashboard,
  Users,
  FileText,
  CheckCircle,
  XCircle, 
  Clock,
  TrendingUp,
  Volume2,
  Calendar,
  Activity
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import { format } from 'date-fns';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import PageHeader from '../../components/UI/PageHeader';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import StatusBadge from '../../components/UI/StatusBadge';

const SUBMISSION_STATUS_TONE = {
  approved: 'success',
  rejected: 'danger',
  in_review: 'info',
};

const AdminDashboardPage = () => {
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });

  // Fetch dashboard statistics
  const { data: dashboardData, isLoading, error, refetch } = useQuery(
    ['dashboard-stats', dateRange],
    () => {
      // Only send date params if they have values
      const params = {};
      if (dateRange.startDate) params.startDate = dateRange.startDate;
      if (dateRange.endDate) params.endDate = dateRange.endDate;
      return adminAPI.getDashboardStats(params);
    },
    {
      refetchOnWindowFocus: false,
      refetchInterval: 30000, // Refresh every 30 seconds
    }
  );

  const stats = dashboardData?.data?.data?.statistics || {};
  const recentActivity = dashboardData?.data?.data?.recentActivity || {};

  // Stats cards configuration
  const statsCards = [
    {
      title: 'Total Submissions',
      value: stats.submissions?.total || 0,
      icon: FileText,
      color: 'bg-blue-500',
      textColor: 'text-blue-600',
      bgColor: 'bg-blue-50',
      description: 'All submissions'
    },
    {
      title: 'Pending Review',
      value: stats.submissions?.pending || 0,
      icon: Clock,
      color: 'bg-yellow-500',
      textColor: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      description: 'Awaiting approval'
    },
    {
      title: 'Approved',
      value: stats.submissions?.approved || 0,
      icon: CheckCircle,
      color: 'bg-green-500',
      textColor: 'text-green-600',
      bgColor: 'bg-green-50',
      description: 'Successfully approved'
    },
    {
      title: 'Rejected',
      value: stats.submissions?.rejected || 0,
      icon: XCircle,
      color: 'bg-red-500',
      textColor: 'text-red-600',
      bgColor: 'bg-red-50',
      description: 'Declined submissions'
    },
    {
      title: 'Total Users',
      value: stats.users?.total || 0,
      icon: Users,
      color: 'bg-purple-500',
      textColor: 'text-purple-600',
      bgColor: 'bg-purple-50',
      description: `${stats.users?.active || 0} active`
    },
    {
      title: 'Active Templates',
      value: stats.templates?.total || 0,
      icon: LayoutDashboard,
      color: 'bg-indigo-500',
      textColor: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      description: 'Available templates'
    },
    {
      title: 'Audio Files',
      value: stats.audio?.total || 0,
      icon: Volume2,
      color: 'bg-pink-500',
      textColor: 'text-pink-600',
      bgColor: 'bg-pink-50',
      description: 'Generated audio'
    },
    {
      title: 'Approval Rate',
      value: `${stats.submissions?.approvalRate || 0}%`,
      icon: TrendingUp,
      color: 'bg-teal-500',
      textColor: 'text-teal-600',
      bgColor: 'bg-teal-50',
      description: 'Success rate'
    },
  ];

  const handleDateFilterChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const clearFilters = () => {
    setDateRange({ startDate: '', endDate: '' });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Failed to load dashboard statistics. Please try again.</p>
        <button 
          onClick={() => refetch()}
          className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Admin Dashboard"
        subtitle="Overview of system activity and management tools."
        actions={<Button variant="ghost" icon={Activity} onClick={() => refetch()}>Refresh</Button>}
      />

      {/* Date Filter */}
      <Card>
        <div className="flex flex-col sm:flex-row sm:items-end space-y-3 sm:space-y-0 sm:space-x-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              name="startDate"
              value={dateRange.startDate}
              onChange={handleDateFilterChange}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              name="endDate"
              value={dateRange.endDate}
              onChange={handleDateFilterChange}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>
          {(dateRange.startDate || dateRange.endDate) && (
            <Button variant="ghost" onClick={clearFilters}>Clear Filters</Button>
          )}
        </div>
      </Card>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {statsCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} interactive>
              <div className="flex items-center">
                <div className={`flex-shrink-0 ${stat.bgColor} rounded-md p-3`}>
                  <Icon className={`h-6 w-6 ${stat.textColor}`} />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      {stat.title}
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900">
                        {stat.value}
                      </div>
                    </dd>
                    <dd className="text-xs text-gray-500 mt-1">
                      {stat.description}
                    </dd>
                  </dl>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Recent Submissions */}
        <Card padded={false}>
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Recent Submissions
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Latest 5 submission requests
            </p>
          </div>
          <div className="px-4 py-5 sm:p-6">
            {recentActivity.submissions && recentActivity.submissions.length > 0 ? (
              <div className="space-y-4">
                {recentActivity.submissions.map((submission) => (
                  <div
                    key={submission.id}
                    className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className={`flex-shrink-0 mt-1 ${
                      submission.status === 'approved' ? 'text-green-500' :
                      submission.status === 'rejected' ? 'text-red-500' :
                      submission.status === 'in_review' ? 'text-blue-500' :
                      'text-yellow-500'
                    }`}>
                      {submission.status === 'approved' ? <CheckCircle className="h-5 w-5" /> :
                       submission.status === 'rejected' ? <XCircle className="h-5 w-5" /> :
                       <Clock className="h-5 w-5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {submission.title || submission.text?.substring(0, 50) || 'Untitled'}
                      </p>
                      <p className="text-sm text-gray-500">
                        by {submission.user?.name || 'Unknown'}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        <Calendar className="h-3 w-3 inline mr-1" />
                        {format(new Date(submission.createdAt), 'MMM dd, yyyy HH:mm')}
                      </p>
                    </div>
                    <StatusBadge label={submission.status} tone={SUBMISSION_STATUS_TONE[submission.status] || 'warning'} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <FileText className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2 text-sm text-gray-500">No recent submissions</p>
              </div>
            )}
          </div>
        </Card>

        {/* Recent Audio Files */}
        <Card padded={false}>
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Recent Audio Files
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Latest generated audio files
            </p>
          </div>
          <div className="px-4 py-5 sm:p-6">
            {recentActivity.audioFiles && recentActivity.audioFiles.length > 0 ? (
              <div className="space-y-4">
                {recentActivity.audioFiles.map((audio) => (
                  <div
                    key={audio.id}
                    className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex-shrink-0 mt-1">
                      <div className="bg-purple-100 rounded-md p-2">
                        <Volume2 className="h-5 w-5 text-purple-600" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {audio.filename || 'Audio File'}
                      </p>
                      <p className="text-sm text-gray-500">
                        Voice: {audio.voice || 'Default'} | Speed: {audio.speed || 1.0}x
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        <Calendar className="h-3 w-3 inline mr-1" />
                        {format(new Date(audio.createdAt), 'MMM dd, yyyy HH:mm')}
                      </p>
                    </div>
                    {audio.duration && (
                      <div className="text-xs text-gray-500">
                        {audio.duration}s
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <Volume2 className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2 text-sm text-gray-500">No audio files generated yet</p>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card padded={false}>
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Quick Actions
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Common administrative tasks
          </p>
        </div>
        <div className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <a
              href="/admin/airport-announcements"
              className="relative rounded-lg border border-gray-300 bg-white px-6 py-5 shadow-sm flex items-center space-x-3 hover:border-gray-400 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500"
            >
              <div className="flex-shrink-0">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">Review Pending</p>
                <p className="text-xs text-gray-500">{stats.submissions?.pending || 0} items</p>
              </div>
            </a>

            <a
              href="/admin/users"
              className="relative rounded-lg border border-gray-300 bg-white px-6 py-5 shadow-sm flex items-center space-x-3 hover:border-gray-400 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500"
            >
              <div className="flex-shrink-0">
                <Users className="h-6 w-6 text-purple-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">Manage Users</p>
                <p className="text-xs text-gray-500">{stats.users?.total || 0} users</p>
              </div>
            </a>

            <a
              href="/admin/templates"
              className="relative rounded-lg border border-gray-300 bg-white px-6 py-5 shadow-sm flex items-center space-x-3 hover:border-gray-400 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500"
            >
              <div className="flex-shrink-0">
                <LayoutDashboard className="h-6 w-6 text-indigo-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">Templates</p>
                <p className="text-xs text-gray-500">{stats.templates?.total || 0} active</p>
              </div>
            </a>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AdminDashboardPage;